<script setup lang="ts">
import CloseButton from '@zyro-inc/site-modules/components/CloseButton.vue';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import {
	computed,
	onBeforeUnmount,
	onMounted,
	ref,
} from 'vue';

const modal = ref<HTMLDivElement | null>(null);

const { closeEcommerceModal } = useEcommerceModal();

const props = withDefaults(defineProps<{
	backgroundColor?: string;
	padding?: string;
	width?: string;
	maxWidth?: string;
	overflow?: string;
	height?: string;
	mobileHeight?: string;
	popStateOnClose?: boolean;
}>(), {
	backgroundColor: 'var(--color-light)',
	padding: '16px',
	width: 'auto',
	maxWidth: 'unset',
	overflow: 'unset',
	height: 'auto',
	mobileHeight: '',
	popStateOnClose: true,
});

const modalStyle = computed(() => ({
	'--modal-background-color': props.backgroundColor,
	'--padding': props.padding,
	'--width': props.width,
	'--max-width': props.maxWidth,
	'--overflow': props.overflow,
	'--height': props.height,
	'--mobile-height': props.mobileHeight || props.height,
}));

const handleCloseModalAction = () => {
	closeEcommerceModal();

	if (props.popStateOnClose) {
		const params = new URLSearchParams(window.location.search);

		if (!params.get('open-modal')) return;
		window.history.back();
	}
};

onMounted(() => {
	modal.value?.focus();

	window.addEventListener('popstate', handleCloseModalAction);
});

onBeforeUnmount(() => {
	window.removeEventListener('popstate', handleCloseModalAction);
});
</script>

<template>
	<Transition name="fade">
		<div
			v-show="true"
			class="modal-wrapper"
		>
			<div
				tabindex="-1"
				class="modal-backdrop"
				@click="handleCloseModalAction"
			/>
			<div
				ref="modal"
				:style="modalStyle"
				class="modal"
				tabindex="0"
				aria-modal="true"
				@keydown.esc="handleCloseModalAction"
			>
				<CloseButton
					class="modal__close-button"
					@click="handleCloseModalAction"
				/>
				<slot />
			</div>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

.modal-wrapper {
	position: fixed;
	top: 50%;
	left: 50%;
	z-index: $z-index-site-engine-modal-overlay;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100vw;
	height: 100vh;
	transform: translate(-50%, -50%);

	@include site-engine-mobile {
		padding: 0 16px;
		transform: none;
		top: 0;
		height: 100%;
		left: 0;
	}
}

.modal-backdrop {
	position: absolute;
	width: 100%;
	height: 100%;
	cursor: pointer;
	background-color: rgba($color-dark, 0.5);
}

.modal {
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	width: var(--width);
	min-width: 540px;
	max-width: var(--max-width);
	height: var(--height);
	padding: var(--padding);
	overflow: var(--overflow);
	font-family: var(--font-primary);
	text-align: center;
	background-color: var(--modal-background-color);
	outline: none;

	@media screen and (max-width: $media-mobile) {
		min-width: unset;
		height: 100dvh
	}

	&__close-button {
		position: absolute;
		top: 4px;
		right: 4px;
		z-index: 20;
		align-self: flex-end;

		@media screen and (max-width: $media-mobile) {
			position: relative;
		}
	}
}
</style>
